/**
 * Jobs Page — Student-facing job listings board.
 * Shows all active job postings. Apply Now links out to company portal.
 */
import React, { useState, useEffect } from 'react';
import { API_BASE } from '../services/api';

const LOCATION_LABELS = {
  india: { label: 'India (Onsite)', icon: '🇮🇳', color: 'bg-orange-500/20 text-orange-300' },
  remote: { label: 'Remote', icon: '🌐', color: 'bg-blue-500/20 text-blue-300' },
  international: { label: 'International', icon: '✈️', color: 'bg-purple-500/20 text-purple-300' },
};

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'india', label: '🇮🇳 India' },
  { value: 'remote', label: '🌐 Remote' },
  { value: 'international', label: '✈️ International' },
];

const JobsPage = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/jobs`);
        if (!res.ok) throw new Error('Failed to load jobs');
        const data = await res.json();
        setJobs(data);
      } catch {
        setError('Could not load job listings. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    fetchJobs();
  }, []);

  const filtered = filter === 'all' ? jobs : jobs.filter((j) => j.location_type === filter);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-10 px-4">
      <div className="max-w-4xl mx-auto">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">Job Opportunities</h1>
          <p className="text-slate-400 mt-2">
            Curated job openings for GRRAS learners — matching your skills and certifications.
          </p>
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap gap-2 mb-6">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
                filter === f.value
                  ? 'bg-orange-500 text-white shadow-md shadow-orange-500/25'
                  : 'bg-white/10 text-slate-300 hover:bg-white/20'
              }`}
            >
              {f.label}
            </button>
          ))}
          {filtered.length > 0 && (
            <span className="ml-auto text-xs text-slate-500 self-center">
              {filtered.length} {filtered.length === 1 ? 'opening' : 'openings'}
            </span>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500" />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="text-center py-16 text-red-400 text-sm">{error}</div>
        )}

        {/* Empty */}
        {!loading && !error && filtered.length === 0 && (
          <div className="text-center py-20">
            <p className="text-4xl mb-3">💼</p>
            <p className="text-slate-400 text-lg font-medium">No openings right now</p>
            <p className="text-slate-600 text-sm mt-1">
              Enable job alerts in your Profile → Job Settings to get notified when new positions are posted.
            </p>
          </div>
        )}

        {/* Job Cards */}
        <div className="space-y-4">
          {filtered.map((job) => {
            const loc = LOCATION_LABELS[job.location_type] || { label: job.location_type, icon: '📍', color: 'bg-slate-700 text-slate-300' };
            return (
              <div
                key={job.id}
                className="bg-slate-800/60 border border-slate-700/50 rounded-2xl p-6 hover:border-slate-600 transition-all"
              >
                {/* Top row */}
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <h2 className="text-lg font-bold text-white leading-snug">{job.title}</h2>
                    <p className="text-orange-400 font-semibold text-sm mt-0.5">{job.company}</p>
                  </div>

                  {/* Apply button */}
                  <div className="flex-shrink-0">
                    {job.apply_url ? (
                      <a
                        href={job.apply_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-500 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors shadow-md shadow-green-900/30"
                      >
                        Apply Now ↗
                      </a>
                    ) : (
                      <a
                        href="mailto:support@grras.tech"
                        className="inline-flex items-center gap-1.5 bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm font-medium px-5 py-2.5 rounded-xl transition-colors"
                      >
                        Contact Us
                      </a>
                    )}
                  </div>
                </div>

                {/* Tags row */}
                <div className="flex flex-wrap gap-2 mt-3">
                  <span className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium ${loc.color}`}>
                    {loc.icon} {job.city ? `${job.city} · ${loc.label}` : loc.label}
                  </span>
                  {job.salary_range && (
                    <span className="text-xs px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-400 font-medium">
                      💰 {job.salary_range}
                    </span>
                  )}
                  {job.required_certificate && (
                    <span className="text-xs px-2.5 py-1 rounded-full bg-orange-500/20 text-orange-300 font-medium">
                      🏆 {job.required_certificate} Required
                    </span>
                  )}
                  {job.created_at && (
                    <span className="text-xs px-2.5 py-1 rounded-full bg-slate-700/60 text-slate-500">
                      Posted {formatDate(job.created_at)}
                    </span>
                  )}
                </div>

                {/* Description */}
                <p className="text-slate-400 text-sm mt-4 leading-relaxed whitespace-pre-line line-clamp-4">
                  {job.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Bottom note */}
        {!loading && !error && jobs.length > 0 && (
          <p className="text-center text-xs text-slate-600 mt-10">
            Enable job alerts in Profile → Job Settings to get notified about new openings matching your preferences.
          </p>
        )}
      </div>
    </div>
  );
};

export default JobsPage;
